import React, { useState, useEffect, useContext } from "react";
import { AppSettings } from "../../config/app-settings.js";
import * as bootstrap from "bootstrap";
import useAuthStore from "../../stores/auth-store.js";
import Swal from 'sweetalert2';
import ReactDataTable from "../../components/datatables/event-datatables.jsx";
import {
  useEvacuationEvents,
  useCreateEvent,
  useEndEvent
} from "../../hooks/useEvacuation.js";

// Helper function to convert seconds to time format
const convertSecondsToTimeString = (seconds) => {
  if (!seconds) return "00:00";
  const totalSeconds = Math.floor(parseFloat(seconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};

// Transform raw event data to table format
const transformEventData = (events) => {
  if (!events || events.length === 0) return [];

  return events.map(event => {
    const totalVehicles = event.Detailed_Orders?.reduce((acc, order) => {
      if (order.Is_Committed === 1) {
        return acc + (order.Total_Evac_Veh || 0);
      }
      return acc;
    }, 0) || 0;

    const totalPeople = event.Detailed_Orders?.reduce((acc, order) => {
      if (order.Is_Committed === 1) {
        return acc + (order.Total_People || 0);
      }
      return acc;
    }, 0) || 0;

    const numberOfDwellingsUnder = event.Detailed_Orders?.reduce((acc, order) => {
      if (order.Is_Committed === 1) {
        return acc + (order.Total_Occupied_Dwell || 0);
      }
      return acc;
    }, 0) || 0;


    const numberOfAddressesUnder = event.Detailed_Orders?.reduce((acc, order) => {
      if (order.Is_Committed === 1) {
        return acc + (order.Total_Addresses || 0);
      }
      return acc;
    }, 0) || 0;

    const totalEndedOrders = event.Detailed_Orders?.filter(order => order.Is_Rescinded === 1).length || 0;

    const percentageRemaining = totalEndedOrders > 0
      ? (totalEndedOrders / event.Total_Orders * 100).toFixed(2)
      : 0;

    return {
      id: event.ID,
      eventName: event.Title,
      startDate: `${event.Start_Date} ${convertSecondsToTimeString(event.Start_Time)}`,
      createdBy: `${event.Official_FName} ${event.Official_LName}`,
      numberOfZones: event.Total_Orders || 0,
      numberOfPeople: totalPeople,
      numberOfVehicles: totalVehicles,
      numberOfDwellingsUnder: numberOfDwellingsUnder,
      numberOfAddressesUnder: numberOfAddressesUnder,
      evacuationPercentage: percentageRemaining,
      createdAt: event.Created_At,
      officialFirstName: event.Official_FName,
      officialLastName: event.Official_LName,
      comments: event.Comments,
      eventType: event.Event_Type_Name || 'N/A',
      endDate: event.End_Date
        ? `${event.End_Date} ${convertSecondsToTimeString(event.End_Time)}`
        : null,
      status: event.Status,
    };
  });
};

function EvacuationEvent() {
  const context = useContext(AppSettings);
  const userId = useAuthStore((state) => state.user?.id || 0);

  // ==================== REACT QUERY HOOKS ====================
  const {
    data: eventsResponse,
    isLoading,
    error
  } = useEvacuationEvents(userId);

  const createEventMutation = useCreateEvent();
  const endEventMutation = useEndEvent();

  // ==================== LOCAL STATE ====================
  const [modalData, setModalData] = useState({
    title: "",
    startDate: "",
    startTime: "",
    eventTypeId: "",
    typeOther: "",
    officialFname: "",
    officialLname: "",
    comments: ""
  });

  const [endModalData, setEndModalData] = useState({
    officialFname: "",
    officialLname: "",
    comments: "",
    eventId: null,
    eventName: ""
  });

  // ==================== DERIVED STATE ====================
  const events = transformEventData(eventsResponse?.events || []);
  const eventTypes = eventsResponse?.event_types || [];
  const hasActiveEvent = events.some(event => event.status === "Active");

  // ==================== MODAL HANDLERS ====================
  const handleModalInputChange = (e) => {
    const { name, value } = e.target;
    setModalData(prev => ({ ...prev, [name]: value }));
  };

  const resetModalForm = () => {
    setModalData({
      title: "",
      startDate: "",
      startTime: "",
      eventTypeId: "",
      typeOther: "",
      officialFname: "",
      officialLname: "",
      comments: ""
    });
  };

  const handleShowModal = () => {
    resetModalForm();
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const currentTime = now.toTimeString().split(' ')[0].slice(0, 5);

    setModalData(prev => ({
      ...prev,
      startDate: today,
      startTime: currentTime
    }));
  };

  const handleSubmitEvent = async () => {
    // Validate required fields
    if (!modalData.title || !modalData.startDate || !modalData.startTime ||
      !modalData.eventTypeId || !modalData.officialFname || !modalData.officialLname) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please fill in all required fields.',
        confirmButtonColor: '#dc3545'
      });
      return;
    }

    // Validate other field when event type is "other"
    if (modalData.eventTypeId === "100" && !modalData.typeOther) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please specify the event type in the "Other" field.',
        confirmButtonColor: '#dc3545'
      });
      return;
    }

    const eventData = {
      User_ID: userId,
      Title: modalData.title,
      Start_Date: modalData.startDate,
      Start_Time: modalData.startTime,
      End_Date: null,
      End_Time: null,
      Evacuation_Event_Type_ID: modalData.eventTypeId,
      Type_Other: modalData.eventTypeId === "100" ? modalData.typeOther : null,
      Official_Fname: modalData.officialFname,
      Official_LName: modalData.officialLname,
      Comments: modalData.comments || null,
      operation: 'create_event'
    };

    try {
      const response = await createEventMutation.mutateAsync(eventData);

      // Update current event in context if needed
      context.handleSetCurrentEvent(response.event_id || null);

      // Close modal
      const modalElement = document.getElementById('eventCreationModal');
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }

      // Reset form
      resetModalForm();
    } catch (error) {
      // Error handling is done in the mutation's onError
      console.error("Error creating event:", error);
    }
  };

  // ==================== END EVENT HANDLERS ====================
  const handleEndModalInputChange = (e) => {
    const { name, value } = e.target;
    setEndModalData(prev => ({ ...prev, [name]: value }));
  };

  const resetEndModalForm = () => {
    setEndModalData({
      officialFname: "",
      officialLname: "",
      comments: "",
      eventId: null,
      eventName: ""
    });
  };

  const handleShowEndModal = (event) => {
    console.log("handleShowEndModal called with:", event);

    // Check if all orders are ended or if there are no orders
    if (parseInt(event.evacuationPercentage) === 100 || event.numberOfZones === 0) {
      resetEndModalForm();
      setEndModalData(prev => ({
        ...prev,
        eventId: event.id,
        eventName: event.eventName
      }));

      const modalElement = document.getElementById('eventEndModal');
      if (modalElement) {
        const modal = bootstrap.Modal.getInstance(modalElement) || new bootstrap.Modal(modalElement);
        modal.show();
      }
      return;
    }

    Swal.fire({
      icon: 'warning',
      title: 'Cannot End Event',
      text: `Only ${event.evacuationPercentage}% of orders have been ended. All orders must be ended before the event can be closed.`,
      confirmButtonText: 'OK',
      confirmButtonColor: '#3085d6'
    });
  };

  const handleEndEvent = async () => {
    // Validate required fields
    if (!endModalData.officialFname || !endModalData.officialLname) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Please fill in both first name and last name.',
        confirmButtonColor: '#dc3545'
      });
      return;
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Confirm Event End',
      html: `
        <p>Are you sure you want to end this evacuation event?</p>
        <p class="fw-bold">${endModalData.eventName}</p>
        <p class="text-muted">This action cannot be undone.</p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, End Event',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    const endData = {
      officialFName: endModalData.officialFname,
      officialLName: endModalData.officialLname,
      comments: endModalData.comments || null
    };

    try {
      await endEventMutation.mutateAsync({
        eventId: endModalData.eventId,
        endData: endData,
        userId: userId
      });

      // Close modal
      const modalElement = document.getElementById('eventEndModal');
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }

      // Reset form
      resetEndModalForm();
    } catch (error) {
      // Error handling is done in the mutation's onError
      console.error("Error ending event:", error);
    }
  };

  // ==================== LAYOUT EFFECTS ====================
  useEffect(() => {
    context.handleSetAppContentClass('');
    context.handleSetAppSidebarNone(true);
    context.handleSetAppSidebarTwo(false);
    context.handleSetAppSidebarWide(false);
    context.handleSetAppTopMenu(true);

    return () => {
      context.handleSetAppSidebarNone(false);
      context.handleSetAppSidebarTwo(false);
      context.handleSetAppSidebarWide(false);
      context.handleSetAppTopMenu(false);
    };
  }, []);

  // ==================== RENDER ====================
  return (
    <>
      <div className="row gx-0">
        <div className="col-12">
          <div className="d-flex align-items-center mb-3">
            <div>
              <h1 className="page-header mb-0">
                Evacuation Events Table
                <small className="ms-2 text-muted">
                  Management and monitoring of evacuation events
                </small>
              </h1>
            </div>
            <div className="ms-auto">
              {!hasActiveEvent && (
                <button
                  type="button"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#eventCreationModal"
                  onClick={handleShowModal}
                >
                  <i className="fa fa-plus me-2"></i>
                  New Event
                </button>
              )}
            </div>
          </div>

          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <i className="fa fa-list-alt me-2"></i>
                Event History
              </h4>
              <div className="panel-heading-btn">
                <button
                  className="btn btn-xs btn-icon btn-default"
                  onClick={() => window.location.reload()}
                  title="Refresh"
                >
                  <i className="fa fa-refresh"></i>
                </button>
              </div>
            </div>
            <div className="panel-body">
              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="alert alert-danger" role="alert">
                  <i className="fa fa-exclamation-triangle me-2"></i>
                  Error loading events: {error.message}
                </div>
              ) : events.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="fa fa-calendar-o fa-2x mb-2"></i>
                  <div>No evacuation events found</div>
                  <small className="text-muted mt-1 d-block">
                    Create your first evacuation event using the "New Event" button above
                  </small>
                </div>
              ) : (
                <div>
                  <div className="table-responsive">
                    <ReactDataTable
                      data={events}
                      className="table table-striped table-bordered align-middle"
                      onHandleShowEndModal={handleShowEndModal}
                    />
                  </div>
                  <div className="fs-6 mt-4">An Evacuation Event defines an emergency event that may consist of one or more Evacuation Orders.

                    This table shows records of each Evacuation Event, including start and end (if applicable) times, total zones evacuated, total people, vehicles, dwellings involved, and status of the Event.

                    Here, if there is currently no active event, a new event can be created (button on top right).  A form will appear that will need to be filled out (event type, official's information authorizing the event, date-time, etc.) to create the event.

                    Once the event has ended, the event can be closed by pressing the "End Event" button in the event record.  A similar form (as to when the even was created) will appear to document the official closure of this event.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Event Creation Modal */}
      <div className="modal fade" id="eventCreationModal">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">
                <i className="fa fa-plus-circle me-2"></i>
                Create New Evacuation Event
              </h4>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-hidden="true"></button>
            </div>
            <div className="modal-body">
              <form>
                <div className="row">
                  <div className="col-12 mb-3">
                    <label htmlFor="title" className="form-label">
                      Event Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="title"
                      name="title"
                      value={modalData.title}
                      onChange={handleModalInputChange}
                      placeholder="e.g., Wildfire Emergency - West District"
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="startDate" className="form-label">
                      Start Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="startDate"
                      name="startDate"
                      value={modalData.startDate}
                      onChange={handleModalInputChange}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="startTime" className="form-label">
                      Start Time <span className="text-danger">*</span>
                    </label>
                    <input
                      type="time"
                      className="form-control"
                      id="startTime"
                      name="startTime"
                      value={modalData.startTime}
                      onChange={handleModalInputChange}
                      required
                    />
                  </div>

                  <div className="col-md-8 mb-3">
                    <label htmlFor="eventTypeId" className="form-label">
                      Event Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      id="eventTypeId"
                      name="eventTypeId"
                      value={modalData.eventTypeId}
                      onChange={handleModalInputChange}
                      required
                    >
                      <option value="">Select event type...</option>
                      {[...eventTypes].sort((a, b) => (a.ID || a.id) - (b.ID || b.id)).map(type => (
                        <option key={type.ID || type.id} value={type.ID || type.id}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4 mb-3">
                    <label htmlFor="typeOther" className="form-label">
                      Other Type
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="typeOther"
                      name="typeOther"
                      value={modalData.typeOther}
                      onChange={handleModalInputChange}
                      placeholder="Specify if other"
                      disabled={modalData.eventTypeId !== "100"}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="officialFname" className="form-label">
                      Official First Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="officialFname"
                      name="officialFname"
                      value={modalData.officialFname}
                      onChange={handleModalInputChange}
                      placeholder="First name"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="officialLname" className="form-label">
                      Official Last Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="officialLname"
                      name="officialLname"
                      value={modalData.officialLname}
                      onChange={handleModalInputChange}
                      placeholder="Last name"
                      required
                    />
                  </div>

                  <div className="col-12 mb-3">
                    <label htmlFor="comments" className="form-label">
                      Comments
                    </label>
                    <textarea
                      className="form-control"
                      id="comments"
                      name="comments"
                      rows="3"
                      value={modalData.comments}
                      onChange={handleModalInputChange}
                      placeholder="Additional information about the event..."
                    ></textarea>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                <i className="fa fa-times me-2"></i>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleSubmitEvent}
                disabled={createEventMutation.isPending}
              >
                <i className="fa fa-save me-2"></i>
                {createEventMutation.isPending ? 'Creating...' : 'Create Event'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Event End Modal */}
      <div className="modal fade" id="eventEndModal">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header bg-danger text-white">
              <h4 className="modal-title">
                <i className="fa fa-flag-checkered me-2"></i>
                End Evacuation Event
              </h4>
              <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" aria-hidden="true"></button>
            </div>
            <div className="modal-body">
              <div className="alert alert-warning" role="alert">
                <i className="fa fa-exclamation-triangle me-2"></i>
                <strong>Warning:</strong> Ending an evacuation event will mark it as completed. Ensure all evacuation orders have been rescinded before proceeding.
              </div>

              {endModalData.eventName && (
                <div className="alert alert-info" role="alert">
                  <strong>Event:</strong> {endModalData.eventName}
                </div>
              )}

              <form>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="endOfficialFname" className="form-label">
                      Official First Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="endOfficialFname"
                      name="officialFname"
                      value={endModalData.officialFname}
                      onChange={handleEndModalInputChange}
                      placeholder="First name"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="endOfficialLname" className="form-label">
                      Official Last Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="endOfficialLname"
                      name="officialLname"
                      value={endModalData.officialLname}
                      onChange={handleEndModalInputChange}
                      placeholder="Last name"
                      required
                    />
                  </div>

                  <div className="col-12 mb-3">
                    <label htmlFor="endComments" className="form-label">
                      Comments
                    </label>
                    <textarea
                      className="form-control"
                      id="endComments"
                      name="comments"
                      rows="4"
                      value={endModalData.comments}
                      onChange={handleEndModalInputChange}
                      placeholder="Provide any relevant information about ending this evacuation event..."
                    ></textarea>
                    <small className="text-muted">
                      Include reason for ending, current situation status, or any other relevant details.
                    </small>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                <i className="fa fa-times me-2"></i>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleEndEvent}
                disabled={endEventMutation.isPending}
              >
                <i className="fa fa-flag-checkered me-2"></i>
                {endEventMutation.isPending ? 'Ending Event...' : 'End Event'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EvacuationEvent;